<template>
  <div>
    <v-combobox
      v-model="selectedTags"
      :items="availableTags"
      :loading="loading"
      label="Теги проекта"
      hint="Выберите существующие теги или создайте новые"
      persistent-hint
      multiple
      chips
      closable-chips
      variant="outlined"
      item-title="name"
      item-value="id"
      return-object
      class="mt-3"
    >
      <template v-slot:chip="{ props, item }">
        <v-chip
          v-bind="props"
          :color="item.raw.color"
          closable
        >
          <v-icon v-if="item.raw.icon" start size="small">{{ item.raw.icon }}</v-icon>
          {{ item.raw.name }}
        </v-chip>
      </template>

      <template v-slot:item="{ props, item }">
        <v-list-item
          v-bind="props"
          :title="item.raw.name"
          :subtitle="item.raw.description"
        >
          <template v-slot:prepend>
            <v-chip
              :color="item.raw.color"
              size="small"
              class="mr-2"
            >
              <v-icon v-if="item.raw.icon" size="x-small">{{ item.raw.icon }}</v-icon>
            </v-chip>
          </template>
        </v-list-item>
      </template>

      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-title>
            Начните вводить для создания нового тега
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-combobox>

    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      density="compact"
      class="mt-2"
      closable
      @click:close="error = ''"
    >
      {{ error }}
    </v-alert>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import apiClient from '@/apiClient';

// Props and emits
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

// Refs
const selectedTags = ref([]);
const availableTags = ref([]);
const loading = ref(false);
const error = ref('');

// Watch for prop changes (only update if different to avoid loops)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && JSON.stringify(newValue) !== JSON.stringify(selectedTags.value)) {
      selectedTags.value = newValue || [];
    }
  }
);

// Watch for selection changes (only emit if different to avoid loops)
let isUpdating = false;
watch(
  selectedTags,
  async (newTags) => {
    if (isUpdating) return;

    // Handle new tags (strings that don't exist in availableTags)
    const tagObjects = [];

    for (const tag of newTags) {
      if (typeof tag === 'string') {
        // This is a new tag, create it
        try {
          const newTag = await createTag({
            name: tag,
            color: getRandomColor(),
            icon: 'mdi-tag',
          });
          tagObjects.push(newTag);
        } catch (err) {
          console.error('Error creating tag:', err);
          error.value = `Ошибка создания тега "${tag}"`;
        }
      } else {
        // Existing tag
        tagObjects.push(tag);
      }
    }

    // Extract only IDs for backend
    const tagIds = tagObjects.map(tag => typeof tag === 'object' ? tag.id : tag);

    if (JSON.stringify(tagIds) !== JSON.stringify(props.modelValue)) {
      isUpdating = true;
      emit('update:modelValue', tagIds);
      setTimeout(() => { isUpdating = false; }, 0);
    }
  }
);

// Methods
const fetchTags = async () => {
  loading.value = true;
  error.value = '';

  try {
    const response = await apiClient.get('/ref/project-tags');
    availableTags.value = response.data.items || [];
  } catch (err) {
    console.error('Error fetching tags:', err);
    error.value = 'Ошибка загрузки тегов';
  } finally {
    loading.value = false;
  }
};

const createTag = async (tagData) => {
  const response = await apiClient.post('/ref/project-tags', tagData);
  const newTag = response.data;

  // Add to available tags
  availableTags.value.push(newTag);

  return newTag;
};

const getRandomColor = () => {
  const colors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#06B6D4', // cyan
    '#84CC16', // lime
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Lifecycle
onMounted(() => {
  fetchTags();
});
</script>

<style scoped>
/* Component-specific styles */
</style>
